import { create } from "zustand";

interface StoryboardIframeState {
  // URL override for specific storyboards
  overrideUrl: string | null;
  // Refresh key to force iframe reload
  refreshKey: number;
  // Whether iframe should have pointer events
  pointerEventsEnabled: boolean;
  // Opacity settings based on storyboard state
  opacity: number;
  // Background styling
  background: string;
  // Whether storyboard is being dropped
  droppingStoryboard: boolean;
  // Whether storyboard has loaded content
  storyboardLoaded: boolean;
  // Manual loading state
  manualShowLoading: boolean;
}

interface StoryboardIframeStore {
  // Map from storyboard ID to its iframe state
  storyboardIframeStates: { [storyboardId: string]: StoryboardIframeState };

  // Generic action to update any property of storyboard iframe state
  updateStoryboardState: <K extends keyof StoryboardIframeState>(
    storyboardId: string,
    key: K,
    value: StoryboardIframeState[K]
  ) => void;

  // Clear state for a storyboard when it's deleted
  clearStoryboardState: (storyboardId: string) => void;

  // Reset all state
  resetStore: () => void;
}

export const DEFAULT_STORYBOARD_IFRAME_STATE: StoryboardIframeState = {
  overrideUrl: null,
  refreshKey: 0,
  pointerEventsEnabled: true,
  opacity: 1,
  background: "white",
  droppingStoryboard: false,
  storyboardLoaded: false,
  manualShowLoading: false,
};

export const useStoryboardIframeStore = create<StoryboardIframeStore>(
  (set) => ({
    storyboardIframeStates: {},

    updateStoryboardState: (storyboardId, key, value) => {
      set((state) => ({
        storyboardIframeStates: {
          ...state.storyboardIframeStates,
          [storyboardId]: {
            ...DEFAULT_STORYBOARD_IFRAME_STATE,
            ...state.storyboardIframeStates[storyboardId],
            [key]: value,
          },
        },
      }));
    },

    clearStoryboardState: (storyboardId: string) => {
      set((state) => {
        const newStates = { ...state.storyboardIframeStates };
        delete newStates[storyboardId];
        return { storyboardIframeStates: newStates };
      });
    },

    resetStore: () => {
      set({ storyboardIframeStates: {} });
    },
  })
);
